import { NodeType } from '@/enums/node-type.enum';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import { fileKindFromModelAsset } from '@/types/api/assets';
import { MediaToCanvasDragMode, MediaToCanvasDragSource } from '@/utils/mediaToCanvasDrag.utils';
import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import { ImportView } from './ImportView';
import { MediaIteratorView } from './MediaIteratorView';
import { ModelBaseView } from './ModelBaseView';
import type { XYPosition } from 'reactflow';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { ImportData, Node } from '@/types/node';
import type { AiModelData } from '@/types/nodes/model';
import type { FileKind, NodeId } from 'web';

/** Arguments for `FlowView.addImportFromMediaSourceAtFlowPosition` (same `source` / `mode` shapes as media-to-canvas drag payloads). */
export type AddImportFromMediaSourceParams = {
  sourceKind: MediaToCanvasDragSource;
  mode: MediaToCanvasDragMode;
  /** Index into `files` (import, media iterator) or `generations` (AI model). */
  itemIndex: number;
  flowPosition: XYPosition;
  nodeTypes: NodeDefinition[];
};

export function getFileKindFromMediaSourceNode(
  graph: FlowGraph,
  node: Node,
  sourceKind: MediaToCanvasDragSource,
  itemIndex: number,
): FileKind | undefined {
  switch (sourceKind) {
    case MediaToCanvasDragSource.Import: {
      const file = (node.data as ImportData).files[itemIndex];
      return file ?? undefined;
    }
    case MediaToCanvasDragSource.MediaIterator: {
      return new MediaIteratorView(graph, node.id).getFilesList()[itemIndex];
    }
    case MediaToCanvasDragSource.AiModel: {
      const generations = (node.data as AiModelData).generations ?? [];
      const asset = generations[itemIndex];
      return asset ? fileKindFromModelAsset(asset) : undefined;
    }
    default: {
      const _exhaustive: never = sourceKind;
      return _exhaustive;
    }
  }
}

export function getTextFromMediaSourceNode(
  node: Node,
  sourceKind: MediaToCanvasDragSource,
  itemIndex: number,
): string | undefined {
  if (sourceKind !== MediaToCanvasDragSource.AiModel) {
    return undefined;
  }

  const asset = (node.data as AiModelData).generations?.[itemIndex];
  if (asset?.kind.type !== 'text') {
    return undefined;
  }
  return asset.kind.value;
}

/** True when `node` is the workflow node type expected for `sourceKind` (import / media iterator / model). */
export function isNodeTypeForMediaToImportSource(node: Node, sourceKind: MediaToCanvasDragSource): boolean {
  switch (sourceKind) {
    case MediaToCanvasDragSource.Import:
      return node.type === NodeType.Import;
    case MediaToCanvasDragSource.MediaIterator:
      return node.type === NodeType.MediaIterator;
    case MediaToCanvasDragSource.AiModel:
      return isModelNode(node);
    default: {
      const _exhaustive: never = sourceKind;
      return _exhaustive;
    }
  }
}

/** Undo entry for stripping one item from the source node. Caller wraps in a single `graph.edit()`. */
export function buildRemoveMediaFromSourceUndoEntry(
  graph: FlowGraph,
  sourceNodeId: NodeId,
  sourceKind: MediaToCanvasDragSource,
  itemIndex: number,
): UndoRedoEntry | null {
  switch (sourceKind) {
    case MediaToCanvasDragSource.Import:
      return new ImportView(graph, sourceNodeId).removeFileUndoEntry(itemIndex);
    case MediaToCanvasDragSource.MediaIterator:
      return new MediaIteratorView(graph, sourceNodeId).removeFileUndoEntry(itemIndex);
    case MediaToCanvasDragSource.AiModel:
      return new ModelBaseView(graph, sourceNodeId).deleteResultUndoEntry(itemIndex);
    default: {
      const _never: never = sourceKind;
      return _never;
    }
  }
}
